<template>
  <component :is="modalComponent" @close="emit('close')">
    <div
      class="language-modal-wrapper"
      :class="[mode, !isHome && isActiveFaceToFace && mode === 'fill' ? 'rotated' : '']"
    >
      <Indicator v-if="area.length > 1" :indicators="generateIndicators(step, 0, 2)" />

      <section class="container-onboarding-content">
        <div class="wrapper-title">
          <h3 class="title">
            <AnimatedGradientText>{{ textGuide1 }}</AnimatedGradientText>
          </h3>
          <h4>
            <AnimationTyping :text="textGuide2" />
          </h4>
        </div>
        <SingleLanguageList
          :languages="languageStore.interpretationLanguageOptions.textOutputEnabled"
          :selectedLanguage="isHome ? languageHomeLocal : languageAwayLocal"
          :omit="isHome ? languageAway?.code : languageHome!.code"
          @select="onSelect"
        />
      </section>

      <div class="onboarding-footer">
        <button v-if="isVisibleButtonBack" class="button-prev l-btn gray-btn" @click="onClickPrevious">
          {{ localT('single_language_back') }}
        </button>
        <ButtonProcess fullWidth :disabled="isDisabledButtonSelect" @click="onClickSelect">
          {{ localT('single_language_select') }}
        </ButtonProcess>
      </div>
    </div>
  </component>
</template>

<script setup lang="ts">
import type { Component } from 'vue';
import { computed, inject, onMounted, ref, Ref } from 'vue';
import { useI18n } from 'vue-i18n';

import AnimationTyping from '@/components/animations/AnimationTyping.vue';
import SingleLanguageList from '@/components/domains/chat/chat-single/SingleLanguageList.vue';
import AnimatedGradientText from '@/components/shared/base/AnimatedGradientText.vue';
import ButtonProcess from '@/components/shared/base/ButtonProcess.vue';
import Indicator from '@/components/shared/base/Indicator.vue';
import ModalFill from '@/components/shared/composite/ModalFill.vue';
import { getLanguageByCode } from '@/static/languages';
import transformedMessages from '@/static/messageBylanguages.js';
import { useLanguageStore } from '@/stores/language';
import { Language } from '@/types/common';
import { MODAL_COMPONENT_MAP } from '@/utils/constants/common';
import {
  ij_isActiveFaceToFace,
  ij_languageAway,
  ij_languageHome,
  ij_setLanguageAway,
  ij_setLanguageHome,
} from '@/utils/injectionKeys';

const props = withDefaults(
  defineProps<{
    area: string[];
    mode: 'small' | 'fill';
  }>(),
  {
    area: () => [],
    mode: 'fill',
  }
);

const emit = defineEmits<{
  close: [];
  select: [language: Language];
}>();

const { t: localT, locale } = useI18n({
  messages: transformedMessages,
  useScope: 'local',
  inheritLocale: false,
});

const languageStore = useLanguageStore();

const languageHome = inject(ij_languageHome) as Ref<Language | null>;
const languageAway = inject(ij_languageAway) as Ref<Language | null>;
const setLanguageHome = inject(ij_setLanguageHome) as (language: any) => void;
const setLanguageAway = inject(ij_setLanguageAway) as (language: any) => void;
const isActiveFaceToFace = inject(ij_isActiveFaceToFace);

const step = ref(0); // 언어 선택 단계
const languageHomeLocal = ref<Language | null>(
  getLanguageByCode(localStorage.getItem('language') || navigator.language)
);
const languageAwayLocal = ref<Language | null>(null);

// 모달 컨포넌트
const modalComponent = computed<Component>(() => {
  return MODAL_COMPONENT_MAP[props.mode] || ModalFill;
});

// 나의 언어 여부
const isHome = computed<boolean>(() => {
  return props.area[step.value] === 'home';
});

// 이전 버튼 보임 여부
const isVisibleButtonBack = computed<boolean>(() => {
  return step.value > 0 && props.area.length > 1;
});

// 안내문1
const textGuide1 = computed<string>(() => {
  switch (props.area[step.value]) {
    case 'home':
      return localT('single_language_home');
    default:
      return localT('single_language_away');
  }
});

// 안내문 2
const textGuide2 = computed<string>(() => {
  switch (props.area[step.value]) {
    case 'home':
      return localT('please_select_language');
    default:
      return localT('please_select_language_away');
  }
});

// 선택 버튼 비활성화 여부
const isDisabledButtonSelect = computed<boolean>(() => {
  return isHome.value ? languageHomeLocal.value === null : languageAwayLocal.value === null;
});

// 단계 생성
const generateIndicators = (current: number, start: number, count: number): boolean[] => {
  return Array.from({ length: count }, (_, i) => current === start + i);
};

// 언어를 클릭 했을 때
const onSelect = (language: Language) => {
  switch (props.area[step.value]) {
    case 'home':
      languageHomeLocal.value = language;
      locale.value = language.code;
      break;
    case 'away':
      languageAwayLocal.value = language;
      locale.value = language.code;
      break;
  }
};

// 선택 버튼 클릭 시
const onClickSelect = () => {
  if (isHome.value) {
    setLanguageHome(languageHomeLocal.value);
    locale.value = 'en_US';
  } else {
    setLanguageAway(languageAwayLocal.value);
    emit('close');
  }
  if (props.area.length === 1) {
    emit('close');
  } else {
    step.value++;
  }
};

// 이전 버튼 클릭 시
const onClickPrevious = () => {
  step.value--;
  locale.value = languageHomeLocal.value!.code;
};

onMounted(() => {
  if (isHome.value) {
    onSelect(languageHome.value || languageHomeLocal.value!);
  }

  if (!isHome.value && languageAway.value) {
    locale.value = languageAway.value.code;
  }
});
</script>

<style scoped lang="scss">
.language-modal-wrapper {
  display: flex;
  flex-direction: column;
  height: 100dvh;
  justify-content: flex-end;

  &.fill {
    width: 25rem;
    height: 100dvh;
    padding-bottom: 1rem;
    margin: 0 auto;

    @include respond-to(mobile-portrait) {
      width: 100%;
      padding: 1rem;
    }
  }

  &.small {
    height: fit-content;
  }

  :deep(.web-tab-wrap) {
    padding-bottom: 1.5rem;
  }
}

.container-onboarding-content {
  display: flex;
  flex-direction: column;
  padding-bottom: 1rem;
  min-height: 25rem;

  &.fill {
    width: 25rem;
    height: 50dvh;

    @include respond-to(mobile-portrait) {
      width: 100%;
      padding-right: 1.5rem;
      padding-left: 1.5rem;
    }
  }
}

.rotated {
  transform: rotate(180deg);
}

.wrapper-title {
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
}

.onboarding-footer {
  display: flex;
  gap: 0.5rem;

  &.fill {
    width: 25rem;
    margin: 1rem 0;

    @include respond-to(mobile-portrait) {
      width: 100%;
      padding-right: 1.5rem;
      padding-left: 1.5rem;
    }
  }
}

.button-prev {
  width: 30%;
  max-width: 8rem;
}

.btn-full {
  flex: 1;
}

@include respond-to(mobile-portrait) {
  :deep(.modal-content) {
    height: 100dvh;
    border-radius: 0;
    .language-modal-wrapper {
      height: calc(100dvh - 84px);
    }
  }
}
</style>
